import styled from '@emotion/styled'
import isPropValid from '@emotion/is-prop-valid'
import React from 'react'

import {
  FontWeight,
  TextAlignment,
  TextTransform,
  TypographyColor,
  TypographyElement,
  TypographySize,
  TypographyVariant,
} from '../../types/typography'

export type CustomTypographyProps = {
  align?: TextAlignment
  className?: string
  textTransform?: TextTransform
  weight?: FontWeight
  size?: TypographySize
  variant?: TypographyVariant
  color?: TypographyColor
  'data-qa'?: string
}

export type TypographyProps<C extends TypographyElement> = {
  as?: C
} & React.PropsWithChildren<CustomTypographyProps> &
  Omit<React.ComponentPropsWithoutRef<C>, keyof CustomTypographyProps>

export type StyledTypographyProps = {
  align?: TextAlignment
  textTransform?: TextTransform
  weight: FontWeight
  variant: TypographyVariant
  color: TypographyColor
  size: TypographySize
}

const StyledTypography = styled('p', {
  shouldForwardProp: prop =>
    isPropValid(prop) && prop !== 'color' && prop !== 'size',
})<StyledTypographyProps>`
  text-align: ${({ align }) => align};
  text-transform: ${({ textTransform }) => textTransform};
  font-weight: ${({ theme, weight }) => theme.fontWeight[weight]};
  color: ${({ color, theme }) =>
    color === 'inherit'
      ? 'inherit'
      : theme.semantic.typography.color[color].value};
  font-family: ${({ theme, variant }) => theme.fontFamily[variant]};
  font-size: ${({ theme, size }) =>
    theme.typography[size].fontSize.smallScreen};
  line-height: ${({ theme, size }) =>
    theme.typography[size].lineHeight.smallScreen};
  letter-spacing: ${({ theme, size }) =>
    theme.typography[size].letterSpacing.smallScreen};

  @media (min-width: ${({ theme }) => theme.breakpoint.md}) {
    font-family: ${({ theme, variant }) => theme.fontFamily[variant]};
    font-size: ${({ theme, size }) =>
      theme.typography[size].fontSize.largeScreen};
    line-height: ${({ theme, size }) =>
      theme.typography[size].lineHeight.largeScreen};
    letter-spacing: ${({ theme, size }) =>
      theme.typography[size].letterSpacing.largeScreen};
  }
`

export const Typography = <C extends TypographyElement>({
  align,
  as,
  children,
  className,
  color = 'inherit',
  'data-qa': dataQa,
  textTransform,
  size = '100',
  variant = 'body',
  weight = 'regular',
  ...htmlGlobalAttributes
}: TypographyProps<C>) => (
  <StyledTypography
    align={align}
    as={as}
    className={className}
    color={color}
    data-qa={dataQa}
    textTransform={textTransform}
    size={size}
    variant={variant}
    weight={weight}
    {...htmlGlobalAttributes}
  >
    {children}
  </StyledTypography>
)

Typography.displayName = 'Typography'
